/**
 * Error classification utility for API errors
 * Based on backend error response structure
 */

export interface ApiError {
  status?: boolean;
  hasError?: boolean;
  statusCode: number;
  message: string;
  code?: string;
  action?: "RECONNECT_UNIPILE" | "RETRY" | string;
  isProviderAccountIssue?: boolean;
  provider?: "unipile" | "linkedin" | "whatsapp" | "email";
  providerError?: any;
}

export interface ErrorClassification {
  type: "USER_SESSION" | "PROVIDER_ACCOUNT" | "TIMEOUT" | "RATE_LIMIT" | "OTHER";
  shouldLogout: boolean;
  action?: string;
  provider?: string;
  message?: string;
}

/**
 * Classifies an API error based on status code and flags
 */
export function classifyError(error: ApiError | any): ErrorClassification {
  // Extract error data from axios error response
  const apiError: ApiError = error?.response?.data || error;
  const statusCode = apiError.statusCode || error?.response?.status || 500;
  const isProviderAccountIssue = apiError.isProviderAccountIssue === true;
  const action = apiError.action;
  const provider = apiError.provider;
  const message = apiError.message;

  // Provider account issue (422) - DO NOT log out
  if (statusCode === 422 && isProviderAccountIssue === true) {
    return {
      type: "PROVIDER_ACCOUNT",
      shouldLogout: false,
      action: action || "RECONNECT_UNIPILE",
      provider: provider,
      message: message,
    };
  }

  // User session issue (401) - LOG OUT only if NOT a provider account issue
  // Important: If isProviderAccountIssue is true, it's a provider error, NOT a user session error
  if (statusCode === 401 && isProviderAccountIssue !== true) {
    return {
      type: "USER_SESSION",
      shouldLogout: true,
      message: message,
    };
  }

  // Edge case: 401 with isProviderAccountIssue=true should be treated as provider account error
  // (This shouldn't happen per backend design, but handle it defensively)
  if (statusCode === 401 && isProviderAccountIssue === true) {
    return {
      type: "PROVIDER_ACCOUNT",
      shouldLogout: false,
      action: action || "RECONNECT_UNIPILE",
      provider: provider,
      message: message,
    };
  }

  // Timeout (504) - suggest retry
  if (statusCode === 504 && action === "RETRY") {
    return {
      type: "TIMEOUT",
      shouldLogout: false,
      action: "RETRY",
      provider: provider,
      message: message,
    };
  }

  // Rate limit (429)
  if (statusCode === 429) {
    return {
      type: "RATE_LIMIT",
      shouldLogout: false,
      message: message || "Too many requests. Please wait a moment.",
    };
  }

  // Other errors
  return {
    type: "OTHER",
    shouldLogout: false,
    message: message,
  };
}

/**
 * Extracts account ID from request config/URL if possible
 */
export function extractAccountIdFromRequest(error: any): string | null {
  if (!error?.config) return null;

  const url = error.config.url || "";
  const params = error.config.params || {};
  const data = error.config.data || {};

  // Try to extract from URL params
  const accountIdMatch = url.match(/account[_-]?id[=:]([^&\/]+)/i);
  if (accountIdMatch) return accountIdMatch[1];

  // Try to extract from query params
  if (params.account_id) return params.account_id;
  if (params.accountId) return params.accountId;

  // Try to extract from request body
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed.account_id) return parsed.account_id;
      if (parsed.accountId) return parsed.accountId;
    } catch (e) {
      // Ignore parse errors
    }
  } else if (data?.account_id) {
    return data.account_id;
  } else if (data?.accountId) {
    return data.accountId;
  }

  return null;
}
